import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api from "../utils/api";
import Linkify from "linkify-react";
import { parseMarkdownLinks } from "../utils/parseMarkdownLinks";

const EventsNews = () => {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);
  const [eventsSearchTerm, setEventsSearchTerm] = useState("");
  const [newsSearchTerm, setNewsSearchTerm] = useState("");
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [expandedArticles, setExpandedArticles] = useState(new Set());

  useEffect(() => {
    fetchEvents();
    fetchNews();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get("/api/events");
      setEvents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchNews = async () => {
    try {
      const response = await api.get("/api/news");
      setNews(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setNewsLoading(false);
    }
  };

  const filteredEvents = events.filter(
    (event) =>
      event.title.toLowerCase().includes(eventsSearchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(eventsSearchTerm.toLowerCase())
  );

  const filteredNews = news.filter(
    (article) =>
      article.title.toLowerCase().includes(newsSearchTerm.toLowerCase()) ||
      (article.content &&
        article.content.toLowerCase().includes(newsSearchTerm.toLowerCase()))
  );

  // Toggle expand/collapse for event descriptions
  const toggleEventExpanded = (eventId) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  // Toggle expand/collapse for news articles
  const toggleNewsExpanded = (articleId) => {
    const newExpanded = new Set(expandedArticles);
    if (newExpanded.has(articleId)) {
      newExpanded.delete(articleId);
    } else {
      newExpanded.add(articleId);
    }
    setExpandedArticles(newExpanded);
  };

  // Utility function to handle newlines and links for events
  const formatEventDescription = (description, isExpanded, maxLength = 172) => {
    if (!description) return "No description available.";

    // First, handle newlines by splitting and mapping
    // const lines = description.split("\n");

    // If not expanded, truncate the text
    let displayText = description;
    if (!isExpanded && description.length > maxLength) {
      displayText = description.substring(0, maxLength);
    }

    // Handle newlines in the display text
    const formattedLines = displayText.split("\n").map((line, lineIndex) => (
      <span key={lineIndex}>
        <Linkify options={linkifyOptions}>{line}</Linkify>
        {lineIndex < displayText.split("\n").length - 1 && <br />}
      </span>
    ));

    return formattedLines;
  };

  const linkifyOptions = {
    attributes: {
      class: "text-red-600 hover:text-red-800 underline break-all",
      target: "_blank",
      rel: "noopener noreferrer",
    },
  };

  // Utility function to handle news content
  const getTruncatedNewsContent = (content, articleId, maxLength = 300) => {
    if (!content) return "";

    const isExpanded = expandedArticles.has(articleId);

    // If not expanded and content is longer than maxLength, truncate it
    let displayText = content;
    if (!isExpanded && content.length > maxLength) {
      displayText = content.substring(0, maxLength) + "...";
    }

    // Handle newlines in the display text and make URLs clickable
    const formattedLines = displayText.split("\n").map((line, lineIndex) => (
      <span key={lineIndex}>
        <Linkify options={linkifyOptions}>{line}</Linkify>
        {lineIndex < displayText.split("\n").length - 1 && <br />}
      </span>
    ));

    return formattedLines;
  };

  const shouldShowNewsReadMore = (content, maxLength = 300) => {
    if (!content) return false;
    return content.length > maxLength;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-red-800 to-red-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Events & News</h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed">
            Stay updated with upcoming events and latest news from our church
            community.
          </p>
        </div>
      </section>

      {/* Events Section */}
      <section id="upcoming-events" className="py-20 bg-amber-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Upcoming Events
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
          </div>

          <div className="mb-8">
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search events..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                value={eventsSearchTerm}
                onChange={(e) => setEventsSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {eventsLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading events...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-8">
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event) => (
                  <div
                    key={event._id}
                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    {event.image && (
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <Calendar className="w-5 h-5 text-red-600 mr-2" />
                        <span className="text-sm text-gray-600">
                          {event.date
                            ? new Date(event.date).toISOString().split("T")[0]
                            : "--"}
                        </span>
                      </div>

                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {event.title}
                      </h3>
                      <div className="text-gray-600 mb-4">
                        {formatEventDescription(
                          event.description,
                          expandedEvents.has(event._id)
                        )}
                        {event.description &&
                          event.description.length > 172 && (
                            <button
                              onClick={() => toggleEventExpanded(event._id)}
                              className="flex items-center mt-2 text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                            >
                              {expandedEvents.has(event._id) ? (
                                <>
                                  Show less{" "}
                                  <ChevronUp className="w-4 h-4 ml-1" />
                                </>
                              ) : (
                                <>
                                  Read more{" "}
                                  <ChevronDown className="w-4 h-4 ml-1" />
                                </>
                              )}
                            </button>
                          )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="w-4 h-4 mr-2" />
                          <span>{event.time ? event.time : "--"}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="w-4 h-4 mr-2" />
                          <span>
                            {event.location
                              ? parseMarkdownLinks(event.location)
                              : "--"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Events Found
                  </h3>
                  <p className="text-gray-500">
                    {eventsSearchTerm
                      ? "Try adjusting your search terms."
                      : "Check back soon for upcoming events."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* News Section */}
      <section id="news-bulletin" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              News Bulletin
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
          </div>

          <div className="mb-8">
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search news..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                value={newsSearchTerm}
                onChange={(e) => setNewsSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {newsLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading news...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredNews.length > 0 ? (
                filteredNews.map((article) => (
                  <article
                    key={article._id}
                    className="bg-amber-50 rounded-lg shadow-lg overflow-hidden"
                  >
                    <div className="md:flex">
                      {article.image && (
                        <div className="md:w-1/3">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-64 md:h-full object-cover"
                          />
                        </div>
                      )}
                      <div
                        className={`p-8 ${
                          article.image ? "md:w-2/3" : "w-full"
                        }`}
                      >
                        <div className="flex items-center text-sm text-gray-500 mb-4">
                          <Calendar className="w-4 h-4 mr-2" />
                          <span className="mr-4">
                            {new Date(article.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )}
                          </span>
                          {article.createdBy && (
                            <>
                              <User className="w-4 h-4 mr-2" />
                              <span>{article.createdBy.name}</span>
                            </>
                          )}
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                          {article.title}
                        </h2>

                        {article.content && (
                          <>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                              {getTruncatedNewsContent(
                                article.content,
                                article._id
                              )}
                            </p>

                            {shouldShowNewsReadMore(article.content) && (
                              <button
                                onClick={() => toggleNewsExpanded(article._id)}
                                className="flex items-center text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                              >
                                {expandedArticles.has(article._id) ? (
                                  <>
                                    Show less{" "}
                                    <ChevronUp className="w-4 h-4 ml-1" />
                                  </>
                                ) : (
                                  <>
                                    Read more{" "}
                                    <ChevronDown className="w-4 h-4 ml-1" />
                                  </>
                                )}
                              </button>
                            )}
                          </>
                        )}

                        {!article.content && (
                          <p className="text-gray-500 italic mb-4">
                            No content available
                          </p>
                        )}
                      </div>
                    </div>
                  </article>
                ))
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No News Found
                  </h3>
                  <p className="text-gray-500">
                    {newsSearchTerm
                      ? "Try adjusting your search terms."
                      : "Check back soon for updates."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsNews;
